import React, { useState, useEffect, useRef, useMemo } from "react";
import FilterBar from "./FilterBar/FilterBar";
import ChartComponent from "./Charts/ChartComponent";
import NewsFeed from "./NewsFeed/NewsFeed";
import EmgptDiv from "./Emgpt/EmgptDiv";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { Navigate, useLocation } from "react-router-dom";
import Switch from "react-switch";
import {
  fetchDashboardData,
  getDefaultDates,
  initializeFilters,
  downloadDashboardData,
  extractTopSentiments,
  fetchDashboardFilter,
  getStoredSelectedFilters,
} from "./DashboardUtils";
import {
  DownloadDashboardDataRequest,
  FetchDashboardDataRequest,
  FetchDashboardFiltersRequest,
} from "./DashboardSerializer";
import {
  loadMoreNews,
  loadNews,
  loadNewsPointClick,
} from "./NewsFeed/NewsFeedUtils";
import { Helmet } from "react-helmet";
import ProductDashboardNoSubscription from "./ProductDashboardNoSubscription";
import { Button, Dropdown, DropdownButton } from "react-bootstrap";
import { FaBars } from "react-icons/fa";
import {
  SeriesReadings,
  SentimentMovers,
  getChartMeta,
} from "./Charts/ChartPanelParts";
import { ProductDeskSkeleton } from "./ProductDashboardSkeletons";
import { readSavedPanelLayout } from "../Common/savedPanelLayout";
import ChartLoader from "../Common/ChartLoader";
import { getUserId } from "../../utilities/authHelper";
import {
  buildDashboardRoute,
  resolveDashboardNameFromSearch,
} from "../../utilities/dashboardRouteUtils";
import WorkspaceSettingsSection from "../Common/WorkspaceSettingsSection";
import WorkspacePresetPicker from "../Common/WorkspacePresetPicker";
import DashboardInfoButton from "./DashboardInfoButton";
import useDashboardPriceOverlay from "./useDashboardPriceOverlay";
import "../CoTrader/CoTrader.css";
import "./ProductDashboard.css";

// Mirrors the CoTrader desk tokens (.ct-page) for the few inline styles left.
const DASHBOARD_WORKSPACE = {
  panelBg: "var(--app-panel-bg, #ffffff)",
  border: "var(--app-border, #e4e8ee)",
  muted: "var(--app-muted, #6b7280)",
  text: "var(--app-text, #111827)",
  accentSoft: "var(--app-accent-soft, rgba(29, 78, 216, 0.08))",
  accentBorder: "var(--app-accent-border, rgba(29, 78, 216, 0.3))",
};

const DASHBOARD_WORKSPACE_VERSION = "v4";
// Bumped when default panel sizes change so saved sizes from the old split do
// not override the new default (workspace presets keep their own key).
const DASHBOARD_LAYOUT_REVISION = "r3";
const DASHBOARD_WORKSPACE_PRODUCT = "product-dashboard";
const DEFAULT_DASHBOARD_WORKSPACE = {
  preset: "desk",
  shellLayout: "stacked",
  chartPosition: "top",
  emgptPosition: "right",
  summaryVisibility: "attached",
};

const DASHBOARD_WORKSPACE_PRESETS = {
  desk: {
    label: "Desk",
    description: "Chart top, Info Flow left, assistant right",
    config: DEFAULT_DASHBOARD_WORKSPACE,
  },
  research: {
    label: "Research",
    description: "Chart top, assistant left, top movers attached",
    config: {
      preset: "research",
      shellLayout: "stacked",
      chartPosition: "top",
      emgptPosition: "left",
      summaryVisibility: "attached",
    },
  },
  split: {
    label: "Split View",
    description: "Chart and Info Flow stack on one side, assistant spans the other",
    config: {
      preset: "split",
      shellLayout: "assistantColumn",
      chartPosition: "top",
      emgptPosition: "right",
      summaryVisibility: "attached",
    },
  },
  briefing: {
    label: "Briefing",
    description: "Chart bottom, Info Flow first, assistant right",
    config: {
      preset: "briefing",
      shellLayout: "stacked",
      chartPosition: "bottom",
      emgptPosition: "right",
      summaryVisibility: "attached",
    },
  },
  tape: {
    label: "Tape",
    description: "Chart hidden, Info Flow first",
    config: {
      preset: "tape",
      shellLayout: "stacked",
      chartPosition: "collapsed",
      emgptPosition: "right",
      summaryVisibility: "hidden",
    },
  },
};

const DASHBOARD_SHELL_LAYOUT_OPTIONS = ["stacked", "assistantColumn"];
const DASHBOARD_CHART_POSITION_OPTIONS = ["top", "bottom", "collapsed"];
const DASHBOARD_EMGPT_POSITION_OPTIONS = ["right", "left", "collapsed"];
const DASHBOARD_SUMMARY_VISIBILITY_OPTIONS = ["attached", "hidden"];

const getDashboardWorkspaceStorageKey = (userId, dashboardKey) =>
  `${DASHBOARD_WORKSPACE_PRODUCT}:workspace:${DASHBOARD_WORKSPACE_VERSION}:${userId || "anonymous"}:${dashboardKey || "default_dashboard"}`;

const getDashboardWorkspacePresetConfig = (presetKey) =>
  DASHBOARD_WORKSPACE_PRESETS[presetKey]?.config || DEFAULT_DASHBOARD_WORKSPACE;

const normalizeDashboardWorkspace = (rawConfig = {}) => {
  const preset =
    rawConfig.preset && DASHBOARD_WORKSPACE_PRESETS[rawConfig.preset]
      ? rawConfig.preset
      : "custom";
  const presetBase =
    preset === "custom"
      ? DEFAULT_DASHBOARD_WORKSPACE
      : getDashboardWorkspacePresetConfig(preset);

  const shellLayout = DASHBOARD_SHELL_LAYOUT_OPTIONS.includes(
    rawConfig.shellLayout
  )
    ? rawConfig.shellLayout
    : presetBase.shellLayout;
  const chartPosition = DASHBOARD_CHART_POSITION_OPTIONS.includes(
    rawConfig.chartPosition
  )
    ? rawConfig.chartPosition
    : presetBase.chartPosition;
  const emgptPosition = DASHBOARD_EMGPT_POSITION_OPTIONS.includes(
    rawConfig.emgptPosition
  )
    ? rawConfig.emgptPosition
    : presetBase.emgptPosition;
  const summaryVisibility = DASHBOARD_SUMMARY_VISIBILITY_OPTIONS.includes(
    rawConfig.summaryVisibility
  )
    ? rawConfig.summaryVisibility
    : presetBase.summaryVisibility;

  return {
    preset,
    shellLayout,
    chartPosition,
    emgptPosition,
    summaryVisibility,
  };
};

const getInitialDashboardWorkspace = (userId, dashboardKey) => {
  try {
    const parsed = JSON.parse(
      localStorage.getItem(getDashboardWorkspaceStorageKey(userId, dashboardKey)) ||
        "null"
    );

    if (parsed) {
      return normalizeDashboardWorkspace(parsed);
    }
  } catch (error) {
    console.warn("Failed to load Product Dashboard workspace config", error);
  }

  return DEFAULT_DASHBOARD_WORKSPACE;
};

// `fixedDashboardName` pins the dashboard instead of reading it from the URL, and
// `initialFilters` ({ selectedFilters, startDate, endDate }) replaces the saved
// filters and default dates at mount; partner embeds of one monitor use both.
function ProductDashboard({ subTabs, fixedDashboardName, initialFilters }) {
  const location = useLocation();
  const dashboard_name =
    fixedDashboardName ?? resolveDashboardNameFromSearch(subTabs, location.search);
  const dashboardKey = dashboard_name || "default_dashboard";
  const currentUserId = useMemo(() => getUserId() || "anonymous", []);
  const localStorageKey = `selectedFilters_${dashboardKey}`;
  const priceOverlayStorageKey = `product-dashboard-price-overlay_${currentUserId}_${dashboardKey}`;
  const workspaceStorageKey = useMemo(
    () => getDashboardWorkspaceStorageKey(currentUserId, dashboardKey),
    [currentUserId, dashboardKey]
  );
  const currentReqKeyRef = useRef(dashboard_name);
  // Each kept-alive tab mounts already pointed at its dashboard; this lets the
  // "dashboard changed" reset skip mount, where it only duplicated fetches.
  const lastDashboardNameRef = useRef(dashboard_name);
  const filterReqSeqRef = useRef(0);
  const dataReqSeqRef = useRef(0);

  const {
    defaultStartDate,
    defaultEndDate,
    filter_companies,
    filter_countries,
    default_embedding,
    start_date_limit,
    end_date_limit,
    ner_relevance_cutoff,
    dashboard_description,
    enable_price_overlay,
    point_click_news_lookback_days,
    default_granularity,
    available_granularities,
  } = getDefaultDates(subTabs, dashboard_name);

  const [filterDate, setFilterDate] = useState({
    endDate: initialFilters?.endDate || defaultEndDate,
    startDate: initialFilters?.startDate || defaultStartDate,
  });

  const [filters, setFilters] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardChartData, setDashboardChartData] = useState(null);
  const [newsList, setNewsList] = useState([]);
  const [isNewsLoading, setIsNewsLoading] = useState(false);
  // First load runs as filters -> chart data -> news. Without a single hold,
  // the skeleton drops between those steps and the real panes (with their own
  // skeletons) flash in a different layout. `isBooting` keeps one skeleton up
  // until the first pass has fully settled.
  const [isBooting, setIsBooting] = useState(true);
  const [bootDataDone, setBootDataDone] = useState(false);
  const [newsStartedFor, setNewsStartedFor] = useState(null);
  const [isMoreNewsLoading, setIsMoreNewsLoading] = useState(false);
  const [noMoreNews, setNoMoreNews] = useState(false);
  const [documentIds, setDocumentIds] = useState([]);
  const [offset, SetOffset] = useState(0);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const [dynamicSuggestions, setDynamicSuggestions] = useState([]);
  const menuRef = useRef(null);
  const [topPositiveSentiments, setTopPositiveSentiments] = useState([]);
  const [topNegativeSentiments, setTopNegativeSentiments] = useState([]);
  const [isStopped, setIsStopped] = useState(false);
  const isStoppedRef = useRef(isStopped);
  const wsRef = useRef(null); // holds the WebSocket instance
  const [selectedNews, setSelectedNews] = useState(null);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [tabs, setTabs] = useState([]);
  const [tvTickers, setTvTickers] = useState([]);
  const filterBarRef = useRef();

  const menuButtonRef = useRef(null);
  const [selectedFilters, setSelectedFilters] = useState(
    () => initialFilters?.selectedFilters || getStoredSelectedFilters(localStorageKey)
  );
  const [isEmgptLoading, setIsEmgptLoading] = useState(false);
  const [hasAskedSummarizePrompt, setHasAskedSummarizePrompt] = useState(false);
  const [downloadButtonLoading, setDownloadButtonLoading] = useState(false);
  const [searchFlag, setSearchFlag] = useState(false);
  const [layoutResetKey, setLayoutResetKey] = useState(0);
  const [chartLayoutVersion, setChartLayoutVersion] = useState(0);
  const [showTopSentiments, setShowTopSentiments] = useState(false);
  const [isEmgptOverlayOpen, setIsEmgptOverlayOpen] = useState(false);
  const [workspaceConfig, setWorkspaceConfig] = useState(() =>
    getInitialDashboardWorkspace(currentUserId, dashboardKey)
  );
  const [selectedDate, setSelectedDate] = useState(null);
  const [dashboardGranularity, setDashboardGranularity] = useState({
    value: default_granularity,
    label: default_granularity,
  });
  const [availableGranularities, setAvailableGranularities] = useState(
    available_granularities
  );
  const {
    canShowPriceOverlay,
    isPriceOverlayLoading,
    priceOverlay,
    priceOverlayTicker,
    showPriceOverlay,
    setShowPriceOverlay,
  } = useDashboardPriceOverlay({
    enabled: enable_price_overlay,
    dashboardChartData,
    filterDate,
    storageKey: priceOverlayStorageKey,
  });
  const hasTopSentiments =
    topPositiveSentiments.length > 0 && topNegativeSentiments.length > 0;
  const dashboardMode = useMemo(
    () => (isEmgptLoading || messages.length > 0 ? "ask" : "monitor"),
    [isEmgptLoading, messages.length]
  );
  const showChart = workspaceConfig.chartPosition !== "collapsed";
  const showEmgpt = workspaceConfig.emgptPosition !== "collapsed";
  const showSentimentSummary = workspaceConfig.summaryVisibility === "attached";
  // Sizes never depend on whether a question is running: asking only moves
  // the focus frame, so panel widths stay put while an answer streams (same
  // as the CoTrader desk).
  const layoutState = useMemo(
    () => ({
      verticalId: `dashboard-vertical-${workspaceConfig.shellLayout}-${workspaceConfig.chartPosition}-${DASHBOARD_WORKSPACE_VERSION}-${DASHBOARD_LAYOUT_REVISION}`,
      horizontalId: `dashboard-horizontal-${workspaceConfig.shellLayout}-${workspaceConfig.emgptPosition}-${DASHBOARD_WORKSPACE_VERSION}-${DASHBOARD_LAYOUT_REVISION}`,
      shellHorizontalId: `dashboard-shell-${workspaceConfig.shellLayout}-${workspaceConfig.emgptPosition}-${DASHBOARD_WORKSPACE_VERSION}-${DASHBOARD_LAYOUT_REVISION}`,
      contentVerticalId: `dashboard-content-${workspaceConfig.shellLayout}-${workspaceConfig.chartPosition}-${DASHBOARD_WORKSPACE_VERSION}-${DASHBOARD_LAYOUT_REVISION}`,
      // The chart handle is always draggable (12%–72%, as in CoTrader).
      chartPanelSize: 50,
      chartPanelMin: 12,
      chartPanelMax: 72,
      bottomPanelSize: 50,
      bottomPanelMin: 28,
      // Info Flow and the assistant split the row evenly by default.
      newsPanelSize: 50,
      newsPanelMin: 30,
      newsPanelMax: 70,
      emgptPanelSize: 50,
      emgptPanelMin: 30,
      contentPanelSize: 50,
      contentPanelMin: 30,
    }),
    [
      workspaceConfig.chartPosition,
      workspaceConfig.emgptPosition,
      workspaceConfig.shellLayout,
    ]
  );

  // Skeleton mirrors the saved pane sizes; re-read each time loading starts
  // (sizes may have been dragged since) or the workspace layout changes.
  const skeletonLayout = useMemo(
    () => (isLoading || isBooting ? {
      row: readSavedPanelLayout(layoutState.horizontalId),
      stack: readSavedPanelLayout(
        workspaceConfig.shellLayout === "assistantColumn"
          ? layoutState.contentVerticalId
          : layoutState.verticalId
      ),
      shell: readSavedPanelLayout(layoutState.shellHorizontalId),
    } : null),
    [isLoading, isBooting, layoutState, workspaceConfig.shellLayout]
  );

  const isinRef = useRef(null);
  const resetLayout = () => {
    if (wsRef.current) {
      setIsStopped(true);
      isStoppedRef.current = true;
      wsRef.current.close();
      wsRef.current = null;
      setMessages([]);
      setDynamicSuggestions([]);
    }
    Object.keys(localStorage)
      .filter(
        (key) =>
          key.startsWith("react-resizable-panels:dashboard-") ||
          key === workspaceStorageKey
      )
      .forEach((key) => localStorage.removeItem(key));
    setWorkspaceConfig(DEFAULT_DASHBOARD_WORKSPACE);
    setLayoutResetKey((prev) => prev + 1);
    setChartLayoutVersion((prev) => prev + 1);
  };
  useEffect(() => {
    if (
      selectedFilters &&
      selectedFilters.filter1 &&
      selectedFilters.filter2?.length > 0 &&
      selectedFilters.filter3?.length > 0
    ) {
      localStorage.setItem(localStorageKey, JSON.stringify(selectedFilters));
    }
    setSelectedPoint(null);
  }, [selectedFilters]);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMenu]);
  const lastWorkspaceKeyRef = useRef(workspaceStorageKey);
  useEffect(() => {
    // Initial state already loaded this workspace; re-applying it on mount
    // only forced a second mount of every panel and chart.
    if (lastWorkspaceKeyRef.current === workspaceStorageKey) return;
    lastWorkspaceKeyRef.current = workspaceStorageKey;
    setWorkspaceConfig(getInitialDashboardWorkspace(currentUserId, dashboardKey));
    setIsEmgptOverlayOpen(false);
    setLayoutResetKey((prev) => prev + 1);
    setChartLayoutVersion((prev) => prev + 1);
  }, [currentUserId, dashboardKey]);
  useEffect(() => {
    return () => {
      isStoppedRef.current = true;
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);
  useEffect(() => {
    localStorage.setItem(workspaceStorageKey, JSON.stringify(workspaceConfig));
  }, [workspaceConfig, workspaceStorageKey]);
  useEffect(() => {
    const requestSeq = ++filterReqSeqRef.current;
    const run = async () => {
      if (dashboard_name !== null) {
        // initializeFilters(
        //   setDashboardData,
        //   setFilters,
        //   setDashboardChartData,
        //   setNewsList,
        //   setDocumentIds,
        //   SetOffset
        // );
        const filterResult = await fetchDashboardFilter({
          fetchDashboardFiltersRequest: new FetchDashboardFiltersRequest(
            dashboard_name,
            filterDate
          ),
          setFilters,
          setSelectedFilters,
          selectedFilters,
          setIsLoading,
          currentReqKeyRef,
          localStorageKey,
          isLatestRequest: () => requestSeq === filterReqSeqRef.current,
        });
        const nextSelected = filterResult?.nextSelectedFilters;
        // No usable selection means no data fetch will follow this one.
        if (!nextSelected?.filter1 || !(nextSelected?.filter2?.length > 0)) {
          setBootDataDone(true);
        }
      } else {
        setIsLoading(false);
        setBootDataDone(true);
      }
    };
    run();
  }, [filterDate]);

  useEffect(() => {
    if (lastDashboardNameRef.current === dashboard_name) return;
    lastDashboardNameRef.current = dashboard_name;
    currentReqKeyRef.current = dashboard_name;
    setDashboardGranularity({
      value: default_granularity,
      label: default_granularity,
    });
    setAvailableGranularities(available_granularities);
    if (wsRef.current) {
      setIsStopped(true);
      isStoppedRef.current = true;
      wsRef.current.close();
      wsRef.current = null;
      setMessages([]);
      setDynamicSuggestions([]);
    }
    if (dashboard_name !== null) {
      // Reset key state objects to avoid carry-over
      initializeFilters(
        setDashboardData,
        setFilters,
        setDashboardChartData,
        setNewsList,
        setDocumentIds,
        SetOffset
      );

      const { defaultStartDate, defaultEndDate } = getDefaultDates(
        subTabs,
        dashboard_name
      );
      setFilterDate({ endDate: defaultEndDate, startDate: defaultStartDate });

      setSelectedFilters(getStoredSelectedFilters(localStorageKey));
    }
  }, [dashboard_name]);

  useEffect(() => {
    if (wsRef.current) {
      setIsStopped(true);
      isStoppedRef.current = true;
      wsRef.current.close();
      wsRef.current = null;
      setMessages([]);
      setDynamicSuggestions([]);
    }
    const requestSeq = ++dataReqSeqRef.current;
    const isLatestRequest = () => requestSeq === dataReqSeqRef.current;
    const runUpdate = async () => {
      if (selectedFilters.filter1 && selectedFilters.filter2.length > 0) {
        await fetchDashboardData({
          fetchDashboardDataRequest: new FetchDashboardDataRequest(
            dashboard_name,
            filterDate,
            selectedFilters.filter2.map((x) => x.value),
            selectedFilters.filter3.map((x) => x.value),
            dashboardGranularity.value
          ),
          selectedFilters,
          setDashboardData,
          setIsLoading,
          currentReqKeyRef,
          setDashboardChartData,
          filterDate,
          isLatestRequest,
        });
        setBootDataDone(true);

        const { topPositives, topNegatives } = await extractTopSentiments(
          dashboard_name,
          selectedFilters.filter1.value
        );
        if (!isLatestRequest()) return;
        setTopPositiveSentiments(topPositives);
        setTopNegativeSentiments(topNegatives);
      }
    };
    runUpdate();
  }, [selectedFilters, filters, dashboardGranularity]);

  useEffect(() => {
    isinRef.current = dashboardChartData;
    if (dashboardChartData) {
      setNewsStartedFor(dashboardChartData);
      loadNews(
        dashboardChartData,
        setNewsList,
        setIsNewsLoading,
        filterDate,
        setDocumentIds,
        SetOffset,
        setNoMoreNews,
        isinRef
      );
    }
  }, [dashboardChartData]);

  useEffect(() => {
    if (!isBooting) return undefined;
    const settled =
      bootDataDone &&
      !isLoading &&
      (dashboardChartData === null ||
        (newsStartedFor === dashboardChartData && !isNewsLoading));
    if (settled) {
      setIsBooting(false);
      return undefined;
    }
    // Failsafe: never hold the skeleton forever if a request stalls.
    const timer = window.setTimeout(() => setIsBooting(false), 12000);
    return () => window.clearTimeout(timer);
  }, [isBooting, bootDataDone, isLoading, dashboardChartData, newsStartedFor, isNewsLoading]);

  const handleLoadMoreNews = () => {
    loadMoreNews({
      dashboardChartData,
      offset,
      setIsMoreNewsLoading,
      setNewsList,
      SetOffset,
      filterDate,
      setNoMoreNews,
      setDocumentIds,
      documentIds,
      newsList,
    });
  };

  const handlePointClick = (data) => {
    setSelectedDate(data.category);
    setSelectedPoint(
      dashboardChartData.filter((x) => x.isin === data.options.isin)[0]
    );
    loadNewsPointClick(
      [data.options.isin],
      setNewsList,
      setIsNewsLoading,
      data.category,
      setDocumentIds,
      setNoMoreNews,
      default_granularity,
      point_click_news_lookback_days
    );
  };

  const handleDownload = (format) => {
    let filterCountries = selectedFilters.filter2.map((x) => x.value);
    let filterCompanies = selectedFilters.filter3.map((x) => x.value);
    downloadDashboardData({
      fetchDashboardDataRequest: new DownloadDashboardDataRequest(
        dashboard_name,
        filterDate,
        format,
        filterCountries,
        filterCompanies
      ),
      setIsLoading: setDownloadButtonLoading,
    });
  };

  const dashboardName = subTabs
    .filter((x) => x.dashboardName === dashboard_name)
    .map((x) => x.name);

  useEffect(() => {
    setHasAskedSummarizePrompt(false);
  }, [dashboard_name, dashboardName[0]]);

  const resetDate = () => {
    setSelectedDate(null);
    setSelectedPoint(null);
    loadNews(
      dashboardChartData,
      setNewsList,
      setIsNewsLoading,
      filterDate,
      setDocumentIds,
      SetOffset,
      setNoMoreNews,
      isinRef
    );
  };

  const applyWorkspacePreset = (presetKey) => {
    setWorkspaceConfig(getDashboardWorkspacePresetConfig(presetKey));
    setLayoutResetKey((prev) => prev + 1);
    setChartLayoutVersion((prev) => prev + 1);
  };

  const updateWorkspaceConfig = (nextConfig) => {
    setWorkspaceConfig((previousConfig) =>
      normalizeDashboardWorkspace({
        ...previousConfig,
        ...nextConfig,
        preset: "custom",
      })
    );
    setLayoutResetKey((prev) => prev + 1);
    setChartLayoutVersion((prev) => prev + 1);
  };

  const currentWorkspaceLabel =
    workspaceConfig.preset === "custom"
      ? "Custom"
      : DASHBOARD_WORKSPACE_PRESETS[workspaceConfig.preset]?.label || "Desk";
  const hiddenModules = [
    showChart
      ? null
      : {
          label: "Show Chart",
          onClick: () => updateWorkspaceConfig({ chartPosition: "top" }),
        },
    showEmgpt
      ? null
      : {
          label: "Show Assistant",
          onClick: () => updateWorkspaceConfig({ emgptPosition: "right" }),
        },
  ].filter(Boolean);

  const chartMeta = getChartMeta(dashboardChartData, dashboardGranularity?.value);

  const renderChartSurface = (paddingClass) => (
    <div className={`${paddingClass} h-100`} style={{ minHeight: 0 }}>
      <section
        className={`ct-panel pd-chart-panel ${
          dashboardMode === "monitor" ? "is-focused" : ""
        }`}
      >
        <header className="pd-panel-head">
          <div className="pd-panel-heading">
            <h2 className="pd-panel-title">Sentiment score</h2>
            {chartMeta && <div className="pd-panel-meta">{chartMeta}</div>}
          </div>
          <SeriesReadings series={dashboardChartData} />
        </header>
        <div className="pd-chart-body" style={{ position: "relative" }}>
          {isPriceOverlayLoading && <ChartLoader dim maxMs={500} label="Loading price history" />}
          <ChartComponent
            dashboardName={dashboard_name}
            dashboardChartData={dashboardChartData}
            legends={true}
            handlePointClick={handlePointClick}
            layoutVersion={chartLayoutVersion}
            priceOverlay={priceOverlay}
          />
        </div>
        {showSentimentSummary && hasTopSentiments && (
          <SentimentMovers
            positives={topPositiveSentiments}
            negatives={topNegativeSentiments}
            onSelect={(company, country) => {
              filterBarRef.current?.handleSentimentClick(company, country);
            }}
          />
        )}
      </section>
    </div>
  );

  const renderWorkspacePresetPicker = () => (
    <WorkspacePresetPicker
      presets={DASHBOARD_WORKSPACE_PRESETS}
      activePreset={workspaceConfig.preset}
      label={currentWorkspaceLabel}
      onSelect={applyWorkspacePreset}
      onMoreOptions={() => setShowMenu(true)}
    />
  );

  const renderWorkspaceMenuTrigger = () => (
    <div className="position-relative">
      <button
        type="button"
        className="pd-icon-btn"
        onClick={() => setShowMenu(!showMenu)}
        ref={menuButtonRef}
        aria-haspopup="true"
        aria-expanded={showMenu}
        aria-label="Workspace settings"
        title="Workspace settings"
      >
        <FaBars size={15} />
      </button>

      {showMenu && (
        <div ref={menuRef} className="pd-menu" role="menu">
          <div
            className="d-flex flex-column gap-3"
            style={{ flex: 1, minHeight: 0 }}
          >
            {[
              {
                label: "Show Suggestions",
                icon: "fa-lightbulb",
                checked: showSuggestions,
                onChange: () => setShowSuggestions(!showSuggestions),
              },
            ].map(({ label, icon, checked, onChange }) => (
              <div className="pd-menu-row" key={label}>
                <span className="d-flex align-items-center">
                  <i className={`fa ${icon}`} />
                  {label}
                </span>
                <Switch
                  onChange={onChange}
                  checked={checked}
                  height={20}
                  width={40}
                  onColor="#1d4ed8"
                  uncheckedIcon={false}
                  checkedIcon={false}
                />
              </div>
            ))}
            {enable_price_overlay && (
              <div>
                <div className="pd-menu-row">
                  <span className="d-flex align-items-center">
                    <i className="fa fa-chart-line" />
                    Show Price Overlay
                  </span>
                  <Switch
                    onChange={setShowPriceOverlay}
                    checked={showPriceOverlay}
                    disabled={!canShowPriceOverlay}
                    height={20}
                    width={40}
                    onColor="#1d4ed8"
                    uncheckedIcon={false}
                    checkedIcon={false}
                  />
                </div>
                {!canShowPriceOverlay && (
                  <div className="pd-menu-note">Select one line with a price ticker.</div>
                )}
                {isPriceOverlayLoading && (
                  <div className="pd-menu-note">Loading price history…</div>
                )}
              </div>
            )}
            <div
              className="d-flex flex-column gap-3 pe-1"
              style={{
                flex: "1 1 auto",
                minHeight: 0,
                overflowY: "auto",
                overscrollBehavior: "contain",
              }}
            >
              <WorkspaceSettingsSection title="Workspace Presets">
                <div className="d-grid gap-2">
                  {Object.entries(DASHBOARD_WORKSPACE_PRESETS).map(
                    ([presetKey, presetValue]) => {
                      const isActive =
                        workspaceConfig.preset === presetKey &&
                        workspaceConfig.preset !== "custom";

                      return (
                        <button
                          key={presetKey}
                          type="button"
                          className="btn btn-sm text-start rounded-3"
                          style={{
                            background: isActive
                              ? DASHBOARD_WORKSPACE.accentSoft
                              : DASHBOARD_WORKSPACE.panelBg,
                            border: `1px solid ${
                              isActive
                                ? DASHBOARD_WORKSPACE.accentBorder
                                : DASHBOARD_WORKSPACE.border
                            }`,
                            color: DASHBOARD_WORKSPACE.text,
                          }}
                          onClick={() => applyWorkspacePreset(presetKey)}
                        >
                          <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>
                            {presetValue.label}
                          </div>
                          <div
                            style={{
                              color: DASHBOARD_WORKSPACE.muted,
                              fontSize: "0.78rem",
                            }}
                          >
                            {presetValue.description}
                          </div>
                        </button>
                      );
                    }
                  )}
                </div>
              </WorkspaceSettingsSection>

              <WorkspaceSettingsSection title="Module Positions">
                {[
                  {
                    label: "Workspace Shape",
                    value: workspaceConfig.shellLayout,
                    options: [
                      ["stacked", "Stacked"],
                      ["assistantColumn", "Split"],
                    ],
                    onChange: (shellLayout) =>
                      updateWorkspaceConfig({ shellLayout }),
                  },
                  {
                    label: "Chart",
                    value: workspaceConfig.chartPosition,
                    options: [
                      ["top", "Top"],
                      ["bottom", "Bottom"],
                      ["collapsed", "Hidden"],
                    ],
                    onChange: (chartPosition) =>
                      updateWorkspaceConfig({ chartPosition }),
                  },
                  {
                    label: "Assistant",
                    value: workspaceConfig.emgptPosition,
                    options: [
                      ["right", "Right"],
                      ["left", "Left"],
                      ["collapsed", "Hidden"],
                    ],
                    onChange: (emgptPosition) =>
                      updateWorkspaceConfig({ emgptPosition }),
                  },
                  {
                    label: "Top Movers",
                    value: workspaceConfig.summaryVisibility,
                    options: [
                      ["attached", "Attached"],
                      ["hidden", "Hidden"],
                    ],
                    onChange: (summaryVisibility) =>
                      updateWorkspaceConfig({ summaryVisibility }),
                  },
                ].map((control) => (
                  <div key={control.label} className="mb-3">
                    <div className="ct-label mb-2">{control.label}</div>
                    <div className="pd-seg">
                      {control.options.map(([value, label]) => (
                        <button
                          key={value}
                          type="button"
                          className={`pd-seg-btn ${
                            control.value === value ? "is-active" : ""
                          }`}
                          onClick={() => control.onChange(value)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </WorkspaceSettingsSection>
            </div>

            <Button variant="outline-danger" size="sm" onClick={resetLayout}>
              Reset Layout
            </Button>
            <hr className="my-1" />

            <DropdownButton
              id="download-dropdown"
              title={
                <>
                  <i className="fa-solid fa-download me-2" aria-hidden="true" />
                  Download data
                </>
              }
              variant="primary"
              size="sm"
              disabled={downloadButtonLoading}
              className="w-100 pd-download"
              drop="up"
            >
              {["CSV", "JSON", "XLSX"].map((format) => (
                <Dropdown.Item
                  key={format}
                  onClick={() => handleDownload(format)}
                  role="menuitem"
                >
                  {format}
                </Dropdown.Item>
              ))}
            </DropdownButton>
          </div>
        </div>
      )}
    </div>
  );

  const renderInfoFlowSurface = (paddingClass) => (
    <div className={`${paddingClass} h-100`}>
      <NewsFeed
        variant="desk"
        isFocused={dashboardMode === "monitor"}
        newsList={newsList}
        isNewsLoading={isNewsLoading}
        noMoreNews={noMoreNews}
        setDocumentIds={setDocumentIds}
        documentIds={documentIds}
        handleLoadMoreNews={handleLoadMoreNews}
        isMoreNewsLoading={isMoreNewsLoading}
        ner_relevance_cutoff={ner_relevance_cutoff}
        selectedNews={selectedNews}
        setSelectedNews={setSelectedNews}
        resetDate={resetDate}
        selectedPoint={selectedPoint}
        selectedDate={selectedDate}
      />
    </div>
  );

  const renderEmgptSurface = (paddingClass) => (
    <div className={`${paddingClass} h-100`}>
      <div className={`ct-panel ${dashboardMode === "ask" ? "is-focused" : ""}`}>
        <EmgptDiv
          documentIds={documentIds}
          dashboardName={dashboard_name}
          dashboardTitle={dashboardName[0]}
          priceTicker={priceOverlayTicker}
          showPriceOverlay={showPriceOverlay}
          selectedFilters={selectedFilters}
          reference_ids={dashboardChartData ? dashboardChartData.map((x) => x.isin) : []}
          searchFlag={searchFlag}
          setSearchFlag={setSearchFlag}
          default_embedding={default_embedding}
          messages={messages}
          setMessages={setMessages}
          userInput={userInput}
          setUserInput={setUserInput}
          isLoading={isEmgptLoading}
          setIsLoading={setIsEmgptLoading}
          showSuggestions={showSuggestions}
          setDynamicSuggestions={setDynamicSuggestions}
          dynamicSuggestions={dynamicSuggestions}
          isStopped={isStopped}
          setIsStopped={setIsStopped}
          isStoppedRef={isStoppedRef}
          wsRef={wsRef}
          selectedNews={selectedNews}
          newsList={newsList}
          tabs={tabs}
          setTabs={setTabs}
          setTvTickers={setTvTickers}
          selectedDate={selectedDate}
          selectedPoint={selectedPoint}
          point_click_news_lookback_days={point_click_news_lookback_days}
          resetDate={resetDate}
          isOverlayOpen={isEmgptOverlayOpen}
          onOverlayOpenChange={setIsEmgptOverlayOpen}
          hasAskedSummarizePrompt={hasAskedSummarizePrompt}
          setHasAskedSummarizePrompt={setHasAskedSummarizePrompt}
        />
      </div>
    </div>
  );

  // Always available: drag to trade chart height for the lower row. Its 16px
  // strip replaces the gap between the two, so spacing is unchanged.
  const verticalResizeHandle = (
    <PanelResizeHandle className="ct-resize-handle" title="Drag to resize the chart">
      <span className="ct-resize-grip" aria-hidden="true" />
    </PanelResizeHandle>
  );

  const horizontalResizeHandle = (
    <PanelResizeHandle
      style={{
        width: "6px",
        backgroundColor: "transparent",
        cursor: "col-resize",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          width: "4px",
          height: "56px",
          borderRadius: "999px",
          background: "var(--app-grip, #d5dce7)",
        }}
      />
    </PanelResizeHandle>
  );

  // Info Flow beside the assistant (either side), or alone when it is hidden.
  const renderLowerRow = (verticalPadding) => {
    if (!showEmgpt) {
      return renderInfoFlowSurface(`px-3 ${verticalPadding}`);
    }

    const isEmgptLeft = workspaceConfig.emgptPosition === "left";
    const emgptPanel = (
      <Panel
        defaultSize={layoutState.emgptPanelSize}
        minSize={layoutState.emgptPanelMin}
      >
        {renderEmgptSurface(
          `${isEmgptLeft ? "ps-3 pe-2" : "ps-2 pe-3"} ${verticalPadding}`
        )}
      </Panel>
    );

    return (
      <PanelGroup
        direction="horizontal"
        autoSaveId={layoutState.horizontalId}
        key={`${layoutResetKey}-${workspaceConfig.emgptPosition}-horizontal`}
      >
        {isEmgptLeft ? (
          <>
            {emgptPanel}
            {horizontalResizeHandle}
          </>
        ) : null}
        <Panel
          defaultSize={layoutState.newsPanelSize}
          minSize={layoutState.newsPanelMin}
          maxSize={layoutState.newsPanelMax}
        >
          {renderInfoFlowSurface(
            `${isEmgptLeft ? "ps-2 pe-3" : "ps-3 pe-2"} ${verticalPadding}`
          )}
        </Panel>
        {!isEmgptLeft ? (
          <>
            {horizontalResizeHandle}
            {emgptPanel}
          </>
        ) : null}
      </PanelGroup>
    );
  };

  const renderStackedWorkspace = () => {
    if (!showChart) {
      return renderLowerRow("pt-2 pb-3");
    }

    const isChartBottom = workspaceConfig.chartPosition === "bottom";
    const chartPanel = (
      <Panel
        defaultSize={layoutState.chartPanelSize}
        minSize={layoutState.chartPanelMin}
        maxSize={layoutState.chartPanelMax}
      >
        {renderChartSurface(isChartBottom ? "px-3 pt-0 pb-3" : "px-3 pt-2 pb-0")}
      </Panel>
    );
    const lowerPanel = (
      <Panel
        defaultSize={layoutState.bottomPanelSize}
        minSize={layoutState.bottomPanelMin}
      >
        {renderLowerRow(isChartBottom ? "pt-2 pb-0" : "pt-0 pb-3")}
      </Panel>
    );

    return (
      <PanelGroup
        key={`${layoutResetKey}-${workspaceConfig.shellLayout}-${workspaceConfig.chartPosition}`}
        direction="vertical"
        autoSaveId={layoutState.verticalId}
        className="h-100"
        onLayout={() => {
          setChartLayoutVersion((prev) => prev + 1);
        }}
      >
        {isChartBottom ? lowerPanel : chartPanel}
        {verticalResizeHandle}
        {isChartBottom ? chartPanel : lowerPanel}
      </PanelGroup>
    );
  };

  // Chart and Info Flow stacked in one column, with the assistant spanning
  // the full height beside them.
  const renderSplitContentColumn = (sidePadding) => {
    if (!showChart) {
      return renderInfoFlowSurface(`${sidePadding} pt-2 pb-3`);
    }

    const isChartBottom = workspaceConfig.chartPosition === "bottom";
    const chartPanel = (
      <Panel
        defaultSize={layoutState.chartPanelSize}
        minSize={layoutState.chartPanelMin}
        maxSize={layoutState.chartPanelMax}
      >
        {renderChartSurface(`${sidePadding} ${isChartBottom ? "pt-0 pb-3" : "pt-2 pb-0"}`)}
      </Panel>
    );
    const infoPanel = (
      <Panel
        defaultSize={layoutState.bottomPanelSize}
        minSize={layoutState.bottomPanelMin}
      >
        {renderInfoFlowSurface(`${sidePadding} ${isChartBottom ? "pt-2 pb-0" : "pt-0 pb-3"}`)}
      </Panel>
    );

    return (
      <PanelGroup
        direction="vertical"
        autoSaveId={layoutState.contentVerticalId}
        key={`${layoutResetKey}-${workspaceConfig.shellLayout}-${workspaceConfig.chartPosition}-content`}
        className="h-100"
        onLayout={() => {
          setChartLayoutVersion((prev) => prev + 1);
        }}
      >
        {isChartBottom ? infoPanel : chartPanel}
        {verticalResizeHandle}
        {isChartBottom ? chartPanel : infoPanel}
      </PanelGroup>
    );
  };

  const renderAssistantColumnWorkspace = () => {
    if (!showEmgpt) {
      return renderSplitContentColumn("px-3");
    }

    const isEmgptLeft = workspaceConfig.emgptPosition === "left";
    const emgptPanel = (
      <Panel
        defaultSize={layoutState.emgptPanelSize}
        minSize={layoutState.emgptPanelMin}
      >
        {renderEmgptSurface(`${isEmgptLeft ? "ps-3 pe-2" : "ps-2 pe-3"} pt-2 pb-3`)}
      </Panel>
    );

    return (
      <PanelGroup
        direction="horizontal"
        autoSaveId={layoutState.shellHorizontalId}
        key={`${layoutResetKey}-${workspaceConfig.shellLayout}-${workspaceConfig.emgptPosition}-shell`}
        className="h-100"
      >
        {isEmgptLeft ? (
          <>
            {emgptPanel}
            {horizontalResizeHandle}
          </>
        ) : null}
        <Panel
          defaultSize={layoutState.contentPanelSize}
          minSize={layoutState.contentPanelMin}
        >
          {renderSplitContentColumn(isEmgptLeft ? "ps-2 pe-3" : "ps-3 pe-2")}
        </Panel>
        {!isEmgptLeft ? (
          <>
            {horizontalResizeHandle}
            {emgptPanel}
          </>
        ) : null}
      </PanelGroup>
    );
  };

  const hasNoData =
    dashboardChartData === null ||
    (Array.isArray(dashboardChartData) && dashboardChartData.length === 0);

  const renderWorkspace = () => {
    if (isLoading || isBooting) {
      return <ProductDeskSkeleton workspace={workspaceConfig} layout={skeletonLayout} />;
    }
    if (hasNoData) {
      return (
        <div className="ct-empty-state">
          No data is available for the selected date range. Choose another range.
        </div>
      );
    }
    return workspaceConfig.shellLayout === "assistantColumn"
      ? renderAssistantColumnWorkspace()
      : renderStackedWorkspace();
  };

  if (dashboard_name === null && subTabs?.length > 0) {
    return (
      <Navigate
        to={buildDashboardRoute(subTabs[0].dashboardName)}
        replace
      />
    );
  }

  return (
    <>
      <Helmet>
        <title>{dashboardName[0] || "Dashboard"} | EMAlpha</title>
      </Helmet>
      {dashboard_name === null ? (
        <ProductDashboardNoSubscription />
      ) : (
        <div
          className="ct-page pd-page w-100 h-100 d-flex flex-column"
          style={{ minHeight: 0 }}
        >
          <div className="px-3 pt-3 pb-2">
            <div className="ct-chrome pd-chrome pd-chrome-head px-3 py-2">
              <div className="pd-title-group">
                <h1 className="pd-title">{dashboardName[0] || "Dashboard"}</h1>
                <DashboardInfoButton
                  className="pd-about"
                  dashboardName={dashboardName[0] || "Dashboard"}
                  description={dashboard_description}
                />
              </div>
              <div className="pd-chrome-actions">
                {hiddenModules.map((module) => (
                  <button
                    type="button"
                    key={module.label}
                    className="pd-chip-btn"
                    onClick={module.onClick}
                  >
                    <i className="fa-solid fa-plus" aria-hidden="true" />
                    {module.label}
                  </button>
                ))}
                {renderWorkspacePresetPicker()}
                {renderWorkspaceMenuTrigger()}
              </div>
            </div>
          </div>

          <div className="px-3 pb-2">
            <div className="ct-chrome pd-chrome px-3 py-2">
              <FilterBar
                ref={filterBarRef}
                filters={filters}
                filterDate={filterDate}
                setFilterDate={setFilterDate}
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFilters}
                isLoading={isLoading}
                dashboardChartData={dashboardChartData}
                dashboard_name={dashboardName[0]}
                start_date_limit={start_date_limit}
                end_date_limit={end_date_limit}
                setDashboardGranularity={setDashboardGranularity}
                dashboardGranularity={dashboardGranularity}
                availableGranularities={availableGranularities}
              />
            </div>
          </div>

          <div className="d-flex flex-grow-1 flex-column w-100" style={{ minHeight: 0 }}>
            {renderWorkspace()}
          </div>
        </div>
      )}
    </>
  );
}

export default ProductDashboard;
